/**
 * Auth Store - Zustand state management for authentication
 * Handles login, signup, logout, and token management
 * Uses persist middleware to maintain auth state across page refreshes
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { AuthState, AuthTokens, ShopUser, Shop } from '../types';
import { authService, OtpGenerateRequest, OtpGenerateResponse } from '../api/auth';
import { shopManagementService } from '../api/shop-management';
import { apiClient } from '../api/client';
import { configPreloader } from '../services/configurationPreloader';
import { createLogger } from '../config/environment';

const logger = createLogger('AuthStore');

interface AuthActions {
  // Auth actions
  login: (userId: string, password: string) => Promise<void>;
  loginWithOtp: (countryCode: string, mobileNumber: string, otp: string) => Promise<void>;
  generateOtp: (countryCode: string, mobileNumber: string) => Promise<OtpGenerateResponse>;
  logout: () => Promise<void>;
  signup: (shopData: any) => Promise<void>;

  // Token actions
  setTokens: (tokens: AuthTokens) => Promise<void>;
  getTokens: () => Promise<AuthTokens | null>;
  clearTokens: () => Promise<void>;

  // User/Shop actions
  setUser: (user: ShopUser | null) => void;
  setShop: (shop: Shop | null) => void;

  // Status actions
  checkAuthStatus: () => Promise<void>;
  resetError: () => void;
}

export type AuthStore = AuthState & AuthActions;

/**
 * Create auth store with Zustand and persist middleware
 * Persists tokens, user, shop, and isAuthenticated to localStorage
 */
export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      // Initial state
      tokens: null,
      user: null,
      shop: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

  // Login action
  login: async (userId: string, password: string) => {
    set({ isLoading: true, error: null });
    try {
      logger.info('Starting login process');

      const tokens = await authService.login({ userId, password });

      // Store tokens in API client and localStorage
      await apiClient.setTokens(tokens);

      set({
        tokens,
        isAuthenticated: true,
        isLoading: false,
      });

      logger.info('Login successful');

      // Preload configuration data in background to warm up cache
      // This runs async without blocking the login flow
      configPreloader.preloadAll().catch((error) => {
        logger.warn('Background configuration preload failed', error);
      });

      // Load shop data immediately after login to get theme and subscription details
      // Skip for SUPER_ADMIN users (shopId = 'ALL')
      try {
        if (tokens.shopId && tokens.shopId !== 'ALL') {
          logger.info('Loading shop data after login', { shopId: tokens.shopId });
          const shopData = await shopManagementService.getShop(tokens.shopId);
          set({ shop: shopData });
          logger.info('✅ Shop data loaded after login', {
            shopId: tokens.shopId,
            hasTheme: !!shopData?.theme,
            hasSubscriptions: !!shopData?.subscriptions && shopData.subscriptions.length > 0,
          });
        } else {
          logger.info('Skipping shop data load for SUPER_ADMIN user');
        }
      } catch (shopError) {
        logger.warn('Failed to load shop data after login', shopError);
        // Don't throw - allow login to complete even if shop data fails
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Login failed';
      logger.error('Login failed', error);
      set({
        isAuthenticated: false,
        isLoading: false,
        error: errorMessage,
      });
      throw error;
    }
  },

  // Generate OTP for login
  generateOtp: async (countryCode: string, mobileNumber: string): Promise<OtpGenerateResponse> => {
    try {
      logger.info('Generating OTP', { countryCode, mobileNumber });
      const result = await authService.generateOtp({ countryCode, mobileNumber, otpFor: 'auth' });
      return result;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to generate OTP';
      logger.error('OTP generation failed', error);
      throw new Error(errorMessage);
    }
  },

  // Login with OTP
  loginWithOtp: async (countryCode: string, mobileNumber: string, otp: string) => {
    set({ isLoading: true, error: null });
    try {
      logger.info('Starting OTP login process');

      const tokens = await authService.loginWithOtp({ authType: 'otp', countryCode, mobileNumber, otp });

      await apiClient.setTokens(tokens);

      set({
        tokens,
        isAuthenticated: true,
        isLoading: false,
      });

      logger.info('OTP Login successful');

      configPreloader.preloadAll().catch((error) => {
        logger.warn('Background configuration preload failed', error);
      });

      try {
        if (tokens.shopId && tokens.shopId !== 'ALL') {
          logger.info('Loading shop data after OTP login', { shopId: tokens.shopId });
          const shopData = await shopManagementService.getShop(tokens.shopId);
          set({ shop: shopData });
          logger.info('✅ Shop data loaded after OTP login');
        }
      } catch (shopError) {
        logger.warn('Failed to load shop data after OTP login', shopError);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'OTP login failed';
      logger.error('OTP login failed', error);
      set({
        isAuthenticated: false,
        isLoading: false,
        error: errorMessage,
      });
      throw error;
    }
  },

  // Signup action
  signup: async (shopData: any) => {
    set({ isLoading: true, error: null });
    try {
      logger.info('Starting signup process');

      const shop = await authService.createShop(shopData);

      set({
        shop,
        isLoading: false,
      });

      logger.info('Signup successful');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Signup failed';
      logger.error('Signup failed', error);
      set({
        isLoading: false,
        error: errorMessage,
      });
      throw error;
    }
  },

  // Logout action
  logout: async () => {
    set({ isLoading: true, error: null });
    try {
      logger.info('Starting logout process');

      // Clear tokens from storage first
      await authService.logout();

      // Then clear all state
      set({
        tokens: null,
        user: null,
        shop: null,
        isAuthenticated: false,
        isLoading: false,
      });

      logger.info('✅ Logout successful - tokens cleared from localStorage and state reset');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Logout failed';
      logger.error('Logout failed', error);

      // Still clear state even if logout API fails
      set({
        tokens: null,
        user: null,
        shop: null,
        isAuthenticated: false,
        isLoading: false,
        error: errorMessage,
      });

      throw error;
    }
  },

  // Set tokens
  setTokens: async (tokens: AuthTokens) => {
    try {
      await apiClient.setTokens(tokens);
      set({ tokens, isAuthenticated: true });
    } catch (error) {
      logger.error('Failed to set tokens', error);
      throw error;
    }
  },

  // Get tokens
  getTokens: async () => {
    try {
      const tokens = await apiClient.getTokens();
      return tokens;
    } catch (error) {
      logger.error('Failed to get tokens', error);
      return null;
    }
  },

  // Clear tokens
  clearTokens: async () => {
    try {
      await apiClient.clearTokens();
      set({
        tokens: null,
        user: null,
        shop: null,
        isAuthenticated: false,
      });
    } catch (error) {
      logger.error('Failed to clear tokens', error);
    }
  },

  // Set user
  setUser: (user: ShopUser | null) => {
    set({ user });
  },

  // Set shop
  setShop: (shop: Shop | null) => {
    set({ shop });
  },

  // Check auth status on app load
  checkAuthStatus: async () => {
    try {
      logger.info('Checking auth status');

      const tokens = await apiClient.getTokens();
      if (tokens) {
        // Restore tokens to apiClient (important after page refresh)
        await apiClient.setTokens(tokens);

        const isValid = await authService.verifyToken();
        if (isValid) {
          set({ tokens, isAuthenticated: true });
          logger.info('Auth status: authenticated');

          // Preload configuration data in background to warm up cache
          // This runs async without blocking - errors are logged but not thrown
          configPreloader.preloadAll().catch((error) => {
            logger.warn('Background configuration preload failed', error);
          });

          // Load shop data to get theme and other details
          // Skip for SUPER_ADMIN users (shopId = 'ALL')
          try {
            if (tokens.shopId && tokens.shopId !== 'ALL') {
              const shopData = await shopManagementService.getShop(tokens.shopId);
              set({ shop: shopData });
              logger.info('✅ Shop data loaded with theme on app initialization', {
                shopId: tokens.shopId,
                hasTheme: !!shopData?.theme,
              });
            } else {
              logger.info('Skipping shop data load for SUPER_ADMIN user');
            }
          } catch (shopError) {
            logger.warn('Failed to load shop data on auth check', shopError);
          }
        } else {
          await apiClient.clearTokens();
          set({ tokens: null, isAuthenticated: false });
          logger.warn('Stored token is invalid or expired');
        }
      } else {
        set({ tokens: null, isAuthenticated: false });
        logger.info('Auth status: not authenticated');
      }
    } catch (error) {
      logger.error('Failed to check auth status', error);
      set({ isAuthenticated: false });
    }
  },

  // Reset error
  resetError: () => {
    set({ error: null });
  },
}),
    {
      name: 'myshop-auth-storage', // unique name for localStorage key
      storage: createJSONStorage(() => localStorage),
      // Only persist these fields
      partialize: (state) => ({
        tokens: state.tokens,
        user: state.user,
        shop: state.shop,
        isAuthenticated: state.isAuthenticated,
      }),
      // Skip persisting loading and error states
      skipHydration: false,
      // Version for migration if needed in future
      version: 1,
    }
  )
);

/**
 * Helper hook to get auth status
 */
export const useIsAuthenticated = (): boolean => {
  return useAuthStore((state) => state.isAuthenticated);
};

/**
 * Helper hook to get current tokens
 */
export const useAuthTokens = (): AuthTokens | null => {
  return useAuthStore((state) => state.tokens);
};

/**
 * Helper hook to get current user
 */
export const useCurrentUser = (): ShopUser | null => {
  return useAuthStore((state) => state.user);
};

/**
 * Helper hook to get current shop
 */
export const useCurrentShop = (): Shop | null => {
  return useAuthStore((state) => state.shop);
};

